import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ExposedReadCoredataFileSenderRes } from 'app/interfaces/responses/Exposed/exposedReadCoredataFileSenderRes';
import { OtherFundsApplicationsService } from 'app/services/api/applications/other-funds-applications.service';

@Component({
    selector: 'app-coredata-document-input',
    templateUrl: './coredata-document-input.component.html',
    styleUrls: ['./coredata-document-input.component.scss'],
})
export class CoredataDocumentInputComponent {
    @Input() documentGroup: UntypedFormGroup;
    @Input() urlControlName = 'url';
    @Input() fileNameControlName = 'name';
    @Input() showRemove = false;
    @Input() fileNameResolver?: (fileInfo: ExposedReadCoredataFileSenderRes) => string;

    @Output() remove = new EventEmitter<void>();

    loading = false;
    fileNameIsResolved = false;

    constructor(private otherFundsApplicationsService: OtherFundsApplicationsService) {}

    ngOnInit(): void {
        this.setFileNameControlEnabled(false);
    }

    get urlControl(): AbstractControl | null {
        return this.documentGroup?.get(this.urlControlName) ?? null;
    }

    get fileNameControl(): AbstractControl | null {
        return this.documentGroup?.get(this.fileNameControlName) ?? null;
    }

    onUrlInput(): void {
        this.resetFileNameState();
    }

    onUrlBlur(): void {
        this.fetchFileInfo();
    }

    onRemove(): void {
        this.remove.emit();
    }

    private parseFileId(url: string): string {
        const match = url.match(/\/doc\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i);
        return match ? match[1] : '';
    }

    private resetFileNameState(): void {
        this.fileNameControl?.setValue('');
        this.fileNameControl?.markAsUntouched();
        this.loading = false;
        this.fileNameIsResolved = false;
        this.clearFileNameEmptyError();
        this.setFileNameControlEnabled(false);
    }

    private resolveFileName(fileInfo: ExposedReadCoredataFileSenderRes): string {
        if (this.fileNameResolver) {
            return this.fileNameResolver(fileInfo);
        }

        return fileInfo.fileName ?? '';
    }

    private fetchFileInfo(): void {
        this.fileNameIsResolved = false;

        if (!this.urlControl || !this.fileNameControl || this.urlControl.invalid) {
            this.loading = false;
            return;
        }

        const sourceUrl = this.urlControl.value ?? '';
        const fileId = this.parseFileId(sourceUrl);
        if (!fileId) {
            this.loading = false;
            return;
        }

        this.loading = true;
        this.otherFundsApplicationsService
            .getCoredataFileSender(fileId, false, false)
            .subscribe((fileInfo) => {
                if (this.urlControl?.value !== sourceUrl || !fileInfo) {
                    return;
                }

                if (!fileInfo.fileName) {
                    this.urlControl?.setErrors({ fileNameEmpty: true });
                    return;
                }

                this.clearFileNameEmptyError();
                const fileName = this.resolveFileName(fileInfo) ?? '';
                this.fileNameControl.setValue(fileName);
                this.fileNameIsResolved = true;
                this.setFileNameControlEnabled(true);
            })
            .add(() => {
                if (this.urlControl?.value === sourceUrl) {
                    this.loading = false;
                }
            });
    }

    private setFileNameControlEnabled(enabled: boolean): void {
        if (!this.fileNameControl) {
            return;
        }

        if (enabled) {
            this.fileNameControl.enable({ emitEvent: false });
            return;
        }

        this.fileNameControl.disable({ emitEvent: false });
    }

    private clearFileNameEmptyError(): void {
        if (!this.urlControl?.errors?.['fileNameEmpty']) {
            return;
        }

        const { fileNameEmpty, ...rest } = this.urlControl.errors;
        this.urlControl.setErrors(rest);
    }
}
